/**
 * Alert rules settings page (views/settings/alerts.ejs).
 *
 * - "New alert rule" sheet: category → alert type → target → thresholds →
 *   repeat interval → channels, posted to /settings/alerts/subscribe.
 * - Edit sheet (filled from the tapped row by js/ui.js) posts to
 *   /settings/alerts/update/:id; the row toggle posts `enabled` there too.
 * - Delete (row menu or edit sheet) posts to /settings/alerts/delete/:id.
 *
 * All endpoints return JSON { success, message }.
 */
document.addEventListener('DOMContentLoaded', async () => {
  // ─── Sync speed unit preference from localStorage to backend ────────────────
  // Speed thresholds are rendered in the user's unit, so make sure the server
  // agrees with the unit this browser displays before anything is edited.
  const page = document.querySelector('[data-speed-unit]');
  const serverSpeedUnit = page?.dataset.speedUnit || 'km/h';
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem('userPreferences') || '{}');
  } catch {
    stored = {};
  }
  const clientSpeedUnit = stored.speedUnit || 'mph';

  if (clientSpeedUnit !== serverSpeedUnit) {
    try {
      const res = await fetch('/api/user-preferences', {
        method: 'POST',
        body: new URLSearchParams({ speedUnit: clientSpeedUnit }),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const data = await res.json();
      if (data.success) {
        window.location.reload();
        return;
      }
    } catch {
      // continue with server-rendered values if sync fails
    }
  }

  const post = async (url, params = {}) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: new URLSearchParams(params),
    });
    return res.json();
  };

  const toast = (message, type) => window.ui.toast(message, type, type === 'error' ? 5000 : undefined);

  // ─── New rule sheet ─────────────────────────────────────────────────────────
  const addSheet = document.getElementById('rule-add-sheet');
  const subscribeForm = document.getElementById('subscribe-form');

  if (addSheet && subscribeForm) {
    const alertTypeSelect = document.getElementById('alertType');
    const deviceScopeGroup = document.getElementById('deviceScopeGroup');
    const beaconScopeGroup = document.getElementById('beaconScopeGroup');
    const deviceSelect = document.getElementById('deviceId');
    const beaconSelect = document.getElementById('beaconId');
    const thresholdRow = document.getElementById('thresholdRow');
    const receiverRow = document.getElementById('receiverRow');
    const thresholdUnit = document.getElementById('thresholdUnit');
    const thresholdUnitMax = document.getElementById('thresholdUnitMax');
    const minInput = document.getElementById('thresholdMin');
    const maxInput = document.getElementById('thresholdMax');
    const cooldownInput = document.getElementById('cooldownMinutes');

    // Detach the option group that doesn't match the category (hiding an
    // <optgroup> with CSS doesn't work in iOS Safari's picker).
    const groups = {};
    alertTypeSelect.querySelectorAll('optgroup').forEach((group) => (groups[group.dataset.category] = group));

    const currentCategory = () => subscribeForm.querySelector('input[name="alertCategory"]:checked')?.value || 'device';

    const updateAlertTypeInfo = () => {
      const selected = alertTypeSelect.selectedOptions[0];
      if (!selected) return;

      const hasThreshold = selected.dataset.hasThreshold === 'true';
      const unit = selected.dataset.unit || '';
      thresholdRow.hidden = !hasThreshold;
      if (receiverRow) receiverRow.hidden = selected.dataset.receiverOption !== 'true';
      thresholdUnit.textContent = unit ? `(${unit})` : '';
      thresholdUnitMax.textContent = unit ? `(${unit})` : '';
      minInput.placeholder = selected.dataset.defaultMin || 'Optional';
      maxInput.placeholder = selected.dataset.defaultMax || 'Optional';
      // Suggest the repeat interval that suits the alert's severity
      if (selected.dataset.defaultCooldown !== '') cooldownInput.value = selected.dataset.defaultCooldown;
    };

    const updateCategory = () => {
      const isDevice = currentCategory() === 'device';
      Object.entries(groups).forEach(([category, group]) => {
        const wanted = category === (isDevice ? 'device' : 'beacon');
        if (wanted && !group.parentNode) alertTypeSelect.appendChild(group);
        if (!wanted && group.parentNode) group.remove();
      });
      alertTypeSelect.selectedIndex = 0;

      // Only the visible target picker is submitted
      deviceScopeGroup.hidden = !isDevice;
      beaconScopeGroup.hidden = isDevice;
      deviceSelect.disabled = !isDevice;
      beaconSelect.disabled = isDevice;
      if (!isDevice) deviceSelect.value = '';
      if (isDevice) beaconSelect.value = '';

      updateAlertTypeInfo();
    };

    subscribeForm.querySelectorAll('input[name="alertCategory"]').forEach((radio) => radio.addEventListener('change', updateCategory));
    alertTypeSelect.addEventListener('change', updateAlertTypeInfo);
    // The sheet resets its form when opened, so re-sync the dependent fields
    addSheet.addEventListener('ui:sheet-open', updateCategory);
    updateCategory();

    subscribeForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const body = Object.fromEntries(new FormData(subscribeForm));
      // Unticked channels are sent explicitly (the server treats a missing
      // in-app flag as "on")
      body.notifyInApp = subscribeForm.notifyInApp.checked ? 'true' : 'false';
      body.notifyEmail = subscribeForm.notifyEmail.checked ? 'true' : 'false';
      body.notifyRecovery = subscribeForm.notifyRecovery.checked ? 'true' : 'false';
      body.anyReceiver = subscribeForm.anyReceiver?.checked ? 'true' : 'false';
      delete body.alertCategory;

      const submit = subscribeForm.querySelector('[type="submit"]');
      submit.disabled = true;
      try {
        const data = await post('/settings/alerts/subscribe', body);
        if (data.success) {
          toast(data.message || 'Alert rule added');
          window.ui.closeSheet(addSheet);
          setTimeout(() => window.location.reload(), 600);
        } else {
          toast(data.message || 'Could not add the alert rule', 'error');
        }
      } catch (err) {
        console.error(err);
        toast('Network error — please try again', 'error');
      } finally {
        submit.disabled = false;
      }
    });
  }

  // ─── Row on/off toggle ──────────────────────────────────────────────────────
  const setRowEnabled = (li, enabled) => {
    li.dataset.enabled = enabled ? 'yes' : 'no';
    li.querySelector('.ui-row').classList.toggle('is-muted', !enabled);
    const paused = li.querySelector('[data-role="paused"]');
    if (paused) paused.hidden = enabled;
  };

  document.querySelectorAll('.toggle-enabled').forEach((toggle) => {
    toggle.addEventListener('change', async () => {
      const enabled = toggle.checked;
      const li = toggle.closest('li');
      toggle.disabled = true;
      try {
        const data = await post(`/settings/alerts/update/${encodeURIComponent(toggle.dataset.ruleId)}`, { enabled });
        if (!data.success) throw new Error(data.message);
        setRowEnabled(li, enabled);
        toast(enabled ? 'Alert rule turned on' : 'Alert rule paused');
      } catch (err) {
        toggle.checked = !enabled; // revert
        toast(err.message || 'Could not update the alert rule', 'error');
      } finally {
        toggle.disabled = false;
      }
    });
  });

  // ─── Delete ─────────────────────────────────────────────────────────────────
  const ruleList = document.getElementById('rule-list');

  const deleteRule = async (ruleId) => {
    if (!window.confirm('Delete this alert rule? You will stop receiving these alerts.')) return false;
    try {
      const data = await post(`/settings/alerts/delete/${encodeURIComponent(ruleId)}`);
      if (!data.success) throw new Error(data.message);

      ruleList?.querySelector(`li[data-rule-id="${CSS.escape(String(ruleId))}"]`)?.remove();
      const remaining = ruleList ? ruleList.querySelectorAll('li[data-filter-item]').length : 0;
      document.querySelectorAll('[data-filter-count]').forEach((el) => (el.textContent = remaining));
      if (remaining === 0) {
        ruleList?.setAttribute('hidden', '');
        document.querySelector('.ui-toolbar[data-ui-filter]')?.setAttribute('hidden', '');
        document.querySelector('[data-filter-empty]')?.setAttribute('hidden', '');
        document.querySelector('[data-role="no-rules"]')?.removeAttribute('hidden');
      }
      toast(data.message || 'Alert rule deleted');
      return true;
    } catch (err) {
      toast(err.message || 'Could not delete the alert rule', 'error');
      return false;
    }
  };

  document.querySelectorAll('[data-delete-rule]').forEach((btn) => {
    btn.addEventListener('click', () => deleteRule(btn.dataset.deleteRule));
  });

  // ─── Edit sheet ─────────────────────────────────────────────────────────────
  const editSheet = document.getElementById('rule-edit-sheet');
  const editForm = document.getElementById('rule-edit-form');
  if (!editSheet || !editForm) return;

  let editing = null; // { ruleId, hasThreshold, receiverOption }

  editSheet.addEventListener('ui:sheet-open', (e) => {
    const li = e.detail.trigger?.closest('li');
    editing = {
      ruleId: li?.dataset.ruleId,
      hasThreshold: !!e.detail.data?.hasThreshold,
      receiverOption: !!e.detail.data?.receiverOption,
    };
  });

  editSheet.querySelector('[data-role="delete"]').addEventListener('click', async () => {
    if (!editing?.ruleId) return;
    if (await deleteRule(editing.ruleId)) window.ui.closeSheet(editSheet);
  });

  editForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!editing?.ruleId) return;

    const body = {
      notifyInApp: editForm.notifyInApp.checked,
      notifyEmail: editForm.notifyEmail.checked,
      cooldownMinutes: editForm.cooldownMinutes.value,
    };
    if (editing.hasThreshold) {
      body.thresholdMin = editForm.thresholdMin.value;
      body.thresholdMax = editForm.thresholdMax.value;
      body.notifyRecovery = editForm.notifyRecovery.checked;
    }
    if (editing.receiverOption) body.anyReceiver = editForm.anyReceiver.checked;

    const submit = editForm.querySelector('[type="submit"]');
    submit.disabled = true;
    try {
      const data = await post(`/settings/alerts/update/${encodeURIComponent(editing.ruleId)}`, body);
      if (data.success) {
        toast(data.message || 'Alert rule updated');
        window.ui.closeSheet(editSheet);
        setTimeout(() => window.location.reload(), 600);
      } else {
        toast(data.message || 'Could not update the alert rule', 'error');
      }
    } catch (err) {
      console.error(err);
      toast('Network error — please try again', 'error');
    } finally {
      submit.disabled = false;
    }
  });

  // ─── ?rule=<id>: open that rule's edit sheet ────────────────────────────────
  // From an alert's "Edit alert rule" (routes/alertRoutes.js)
  const ruleParam = new URLSearchParams(window.location.search).get('rule');
  if (ruleParam) {
    window.history.replaceState(null, '', window.location.pathname);
    document.querySelector(`#rule-list li[data-rule-id="${CSS.escape(ruleParam)}"] .alert-rule__open`)?.click();
  }
});
